import React, { useCallback, useEffect, useState } from 'react';
import Toolbar from 'src/components/Toolbar';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/Button';
import './style.scoped.css';
import { useHistory } from 'react-router-dom';
import { confirmQuit, formatMoney, showError } from 'src/utils';
import { RequestType, REGEX_BANK_ACCOUNT } from 'src/constants';
import policySt, { PolicyStore } from 'src/stores/PolicyStore';
import { observer } from 'mobx-react-lite';
import InfoItem from 'src/components/InfoItem';
import Checkbox from 'src/components/Checkbox';
import ReasonItemSelector from 'src/components/ReasonItemSelector';
import TextArea from 'src/components/TextArea';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import PopupService from 'src/utils/PopupService';
import InfoIcon from 'src/assets/info_icon.svg';
import TextInput from 'src/components/TextInput';
import UserInfoService from 'src/utils/UserInfoService';
import Dropdown from 'src/components/Dropdown';
dayjs.extend(customParseFormat);

interface CancelPolicyObserverProps {
  policyStore?: PolicyStore;
}

const CancelPolicyObserver = observer(({ policyStore }: CancelPolicyObserverProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [listReason, setListReason] = useState([]);
  const [isAgreeTerm1, setIsAgreeTerm1] = useState(false);
  const [isAgreeTerm2, setIsAgreeTerm2] = useState(false);
  const [otherReason, setOtherReason]= useState("");

  const [isBankAccNo, setIsBankAccNo] = useState<boolean>(false);
  const [inputBankAccNo, setInputBankAccNo]= useState("");
  const [errInputBankAccNo, setErrInputBankAccNo] = useState<string>();
  const [bankAccNo, setBankAccNo] = useState(null);
  const [listBankAccNo, setListBankAccNo] = useState([]);

  const [isBankAccName, setIsBankAccName] = useState<boolean>(false);
  const [inputBankAccName, setInputBankAccName]= useState("");
  const [errInputBankAccName, setErrInputBankAccName] = useState<string>();

  const _handleChangeInputBankAccNo = useCallback((e) => {
    let value = e.target.value;
    if(!value){
      setErrInputBankAccNo(t('account_completion_money_required'));
    }else if(!REGEX_BANK_ACCOUNT.test(value)){
      setErrInputBankAccNo(t('account_completion_money_invalid'));
    }else{
      setErrInputBankAccNo(null);
    }
    setInputBankAccNo(value);
  }, [t]);

  const _handleBlurInputBankAccNo = useCallback((e) => {
    let value = e.target.value?.trim() || "";
    if(!value){
      setErrInputBankAccNo(t('account_completion_money_required'));
    }else if(!REGEX_BANK_ACCOUNT.test(value)){
      setErrInputBankAccNo(t('account_completion_money_invalid'));
    }else{
      setErrInputBankAccNo(null);
    }
    setInputBankAccNo(value);
  }, [t]);

  const _handleChangeInputBankAccName = useCallback((e) => {
    let value = e.target.value;
    if(!value){
      setErrInputBankAccName(t('account_completion_money_name_required'));
    }else{
      setErrInputBankAccName(null);
    }
    setInputBankAccName(value);
  }, [t]);

  const _handleBlurInputBankAccName = useCallback((e) => {
    let value = e.target.value?.trim() || "";
    if(!value){
      setErrInputBankAccName(t('account_completion_money_name_required'));
    }else{
      setErrInputBankAccName(null);
    }
    setInputBankAccName(value);
  }, [t]);

  useEffect(() => {
    if(policyStore.policyToCancel?.requestType == RequestType.FREE_LOOK){
      let mbAccountName = UserInfoService.getUserInfo()?.fullname;
      if(!mbAccountName){
        setIsBankAccName(false);
      }else{
        setIsBankAccName(true);
      }

      let mbAccountList = UserInfoService.getUserInfo()?.mbAccountList;
      let mbAccountDefault = UserInfoService.getUserInfo()?.mbAccountDefault;
      if(!mbAccountList && !mbAccountDefault){
        setIsBankAccNo(false);
      }else{
        setIsBankAccNo(true);
        if(!mbAccountList){
          mbAccountList = [mbAccountDefault];
        }
        if(!mbAccountDefault){
          mbAccountDefault = mbAccountList[0];
        }
        if(!mbAccountList.includes(mbAccountDefault)){
          mbAccountList.unshift(mbAccountDefault);
        }
        setBankAccNo({ value: mbAccountDefault, label: mbAccountDefault });
        setListBankAccNo(mbAccountList.map(account => {
          return { value: account, label: account }
        }));
      }
    }

    setListReason(policyStore.policyToCancel?.listReason ?? []);
  }, [policyStore]);

  const _handleAgreeTerm1 = useCallback(() => {
    setIsAgreeTerm1(!isAgreeTerm1);
  }, [setIsAgreeTerm1, isAgreeTerm1]);

  const _handleAgreeTerm2 = useCallback(() => {
    setIsAgreeTerm2(!isAgreeTerm2);
  }, [setIsAgreeTerm2, isAgreeTerm2]);

  const _handleClose = () => {
    confirmQuit(history, InfoIcon);
  };

  const _handleBack = () => {
    history.goBack();
  };

  const _handleCancelPolicy = () => {
    if(policyStore.policyToCancel?.requestType == RequestType.FREE_LOOK){
      let errorBankAcc = false;
      if(!isBankAccNo){
        if(errInputBankAccNo){
          showError(errInputBankAccNo);
          errorBankAcc = true;
        }
        if(!inputBankAccNo){
          setErrInputBankAccNo(t('account_completion_money_required'));
          errorBankAcc = true;
        }else if(!REGEX_BANK_ACCOUNT.test(inputBankAccNo)){
          setErrInputBankAccNo(t('account_completion_money_invalid'));
          errorBankAcc = true;
        }
      }
      if(!isBankAccName && !inputBankAccName){
        setErrInputBankAccName(t('account_completion_money_name_required'));
        errorBankAcc = true;
      }
      if(errorBankAcc){
        return;
      }
      if(!isAgreeTerm1){
        showError(t('please_confirm_agree_term'));
        return;
      }
    }else if(policyStore.policyToCancel?.requestType == RequestType.AFTER_FREE_LOOK){
      if(!isAgreeTerm1 || !isAgreeTerm2){
        showError(t('please_confirm_agree_term'));
        return;
      }
    }
    let listUnselectedReason = listReason.filter(reason => !reason.checked);
    if(listUnselectedReason.length == listReason.length){
      showError(t('cancel_policy_msg_08'));
      return;
    }
    let otherR = listReason.find(reason => reason.reasonID == "Other");
    if(otherR.checked){
      if(!otherReason?.trim()){
        showError(t('cancel_policy_msg_09'));
        return;
      }
    }

    PopupService.instance.current.open({
      visible: true,
      title: t('notification'),
      content: t('cancel_policy_content', { policyNum: policyStore.policyToCancel?.policyNum }),
      okTitle: t('no'),
      cancelTitle: t('yes'),
      onOk: () => {
        PopupService.instance.current.close();
        history.push("/");
      },
      onCancel: () => {
        PopupService.instance.current.close();
        let params = {
          requestId: policyStore.policyToCancel?.requestId,
          policyNum: policyStore.policyToCancel?.policyNum,
          reqType: policyStore.policyToCancel?.requestType,
          reqReason: null,
          otherReason: null,
          bankAccNo: "",
          bankAccName: "",
        }
        if(policyStore.policyToCancel?.requestType == RequestType.FREE_LOOK){
          if(isBankAccNo){
            params.bankAccNo = bankAccNo?.value || "";
          }else{
            params.bankAccNo = inputBankAccNo;
          }

          if(isBankAccName){
            params.bankAccName = UserInfoService.getUserInfo()?.fullname || "";
          }else{
            params.bankAccName = inputBankAccName;
          }
        }
        let list = [...listReason];
        if(list[list.length - 1].checked){
          params.otherReason = otherReason;
        }
        if(list[list.length - 1]){
          list.pop();
        }
        let reqReason = list.filter(reason => reason.checked);
        reqReason = reqReason.map(reason => {
          return {
            reasonID: reason.reasonID,
            text: reason.text
          }
        });
        params.reqReason = reqReason;
        
        policyStore.setInfoReasonCancelAndCreateOtp({
          infoReasonCancel: params,
          history 
        });
      },
      icon: InfoIcon
    });
  };

  const _handleSelectReason = (reasonID) => {
    let list = [...listReason];
    for(let reason of list){
      if(reason.reasonID == reasonID){
        reason.checked = !reason.checked;
      }
    }
    setListReason(list);
  };

  const _handleChangeReason = (e)=> {
    setOtherReason(e.target.value);
  };

  return (
    <div className="screen-container bg-background view-cancel">
      <Toolbar 
        title={t('cancel_policy')}
        showClose 
        onBack={_handleBack}
        onClose={_handleClose} 
      />
      <div className="max-w-md mx-auto screenScrollContainer">
        <div className='p-4'>
          <div className='confirm-content' dangerouslySetInnerHTML={{ __html: t('confirm_cancel', {
            insurancePolicyNumber: policyStore.policyToCancel?.policyNum ?? "",
            insuranceAmount: formatMoney(policyStore.policyToCancel?.sumAssured ?? "")
          })}}></div>
        </div>
        <div className="wapper_info">
          <div>
            {policyStore.policyToCancel?.requestType == RequestType.FREE_LOOK &&
              <InfoItem
                  title={t('request_type')}
                  content={
                    t("cancel_policy_type_1")}
              />
            }
            {policyStore.policyToCancel?.requestType == RequestType.AFTER_FREE_LOOK &&
              <InfoItem
                  title={t('request_type')}
                  content={
                    t("cancel_policy_type_2")}
              />
            }
          </div>
          <div className='pt-4'>
            <InfoItem
              title={t('request_date')}
              content={dayjs(Date.now()).format(t('date_format1'))}
            />
          </div>
          {policyStore.policyToCancel?.requestType == RequestType.AFTER_FREE_LOOK && <div className='pt-4'>
            <InfoItem
              title={t('termination_contract')}
              content={policyStore.policyToCancel?.terEffectiveDate ?? ""}
            />
          </div>}
          {policyStore.policyToCancel?.requestType == RequestType.FREE_LOOK && <div className='pt-4'>
            {!isBankAccNo && <TextInput
              label={t('account_completion_money')}
              placeholder={t('account_completion_money_hint')}
              value={inputBankAccNo}
              useDisabledStyle={true}
              onChange={_handleChangeInputBankAccNo}
              error={!!errInputBankAccNo}
              helperText={errInputBankAccNo}
              onBlur={_handleBlurInputBankAccNo}
            />}
            {isBankAccNo && <>
              <div className='labelText mb-2'>{t('account_completion_money_1')}</div>
              <Dropdown 
                defaultValue={bankAccNo}
                listValue={listBankAccNo}
                onChange={(value) => setBankAccNo(value)}
                isSearchable={false}
              />
            </>}
          </div>}
          {policyStore.policyToCancel?.requestType == RequestType.FREE_LOOK && <div className='pt-4'>
            {!isBankAccName && <TextInput
              label={t('account_completion_money_name')}
              placeholder={t('account_completion_money_name_hint')}
              value={inputBankAccName}
              useDisabledStyle={true}
              onChange={_handleChangeInputBankAccName}
              error={!!errInputBankAccName}
              helperText={errInputBankAccName}
              onBlur={_handleBlurInputBankAccName}
            />}
            {isBankAccName && <InfoItem
              title={t('account_completion_money_name')}
              content={UserInfoService.getUserInfo()?.fullname || ""}
            />}
          </div>}
        </div>
        {policyStore.policyToCancel?.requestType == RequestType.FREE_LOOK && <div className='pt-4 pl-4 pr-4'>
          <div className="mb-4">
            <Checkbox
              textComponent={
                <div>
                  <div className="text-sm whitespace-pre-wrap flex-1 text-onSurface">
                  {t('agree_term_2')}{' '}
                  <strong>{policyStore.policyToCancel?.policyNum ?? ""}</strong>
                  </div>
                </div>
              }
              checked={isAgreeTerm1}
              onCheck={_handleAgreeTerm1}
              type="card"
            />
          </div>
        </div>}
        {policyStore.policyToCancel?.requestType == RequestType.AFTER_FREE_LOOK && <div className='pt-4 pl-4 pr-4'>
          <div className="mb-4">
            <Checkbox
              text={t('agree_term')}
              checked={isAgreeTerm1}
              onCheck={_handleAgreeTerm1}
              type="card"
            />
          </div>
          <div className="mb-4">
            <Checkbox
              textComponent={
                <div>
                  <div className="text-sm whitespace-pre-wrap flex-1 text-onSurface">
                  {t('agree_term_1')}{' '}
                  <strong>{policyStore.policyToCancel?.policyNum ?? ""}</strong>
                  </div>
                </div>
              }
              checked={isAgreeTerm2}
              onCheck={_handleAgreeTerm2}
              type="card"
            />
          </div>
        </div>}

        <div className='combobox-reason bg-white'>
          <div className='title px-4 pt-4 pb-4 border border-line border-line-bottom'>{t('cancel_reason')} <span className='red'>*</span></div>
          <div className='px-4 pt-5 pb-5'>
            <ReasonItemSelector
              data={listReason}
              onSelect={_handleSelectReason}
            />
            {listReason[listReason.length - 1]?.checked ? <TextArea
              label={t('reason_hint')}
              onChange={_handleChangeReason}
              value={otherReason}
              maxLength={255}
              placeholder={t('please_reason_hint')}
            /> : null}
          </div>
        </div>
        
      </div>
      <div className="bottomButtonBlock">
        <Button
          text={t('confirm_cancel_policy')}
          onClick={_handleCancelPolicy}
          disabled={
            listReason.filter(reason => reason.checked).length == 0
            && (
              (policyStore.policyToCancel?.requestType == RequestType.FREE_LOOK && !isAgreeTerm1)
              || 
              (policyStore.policyToCancel?.requestType == RequestType.AFTER_FREE_LOOK && !isAgreeTerm1 && !isAgreeTerm2)
            )
          }
          extraClassName={'w-full'}
        />
      </div>
    </div>
  );
});

const CancelPolicy = () => <CancelPolicyObserver policyStore={policySt} />;
export default CancelPolicy;
